import { FC, useCallback, useMemo } from 'react';
import { LOG_ACTIONS, logAction } from '@namespace/analytics';
import { useConfig, useGeneralRoutes } from '@namespace/cms';
import {
  SubMenu as CommonSubMenu,
  UserSettings,
  useUserSettings
} from '@namespace/common';
import { useDeviceStore } from '@namespace/device';
import { getRootPath } from '@namespace/helpers';
import { useI18n, useLocation } from '@namespace/i18n';
import { useUserSegmentsIncludes } from '@namespace/segments';
import { useVirtualSportsStore } from '../../store';
import getVSPath from '../../utils/getVSPath';
import {
  IVirtualSportsMenuItemParams,
  IVirtualSportsSubMenuItem,
  IVirtualSubMenu
} from './types';

interface IProps {
  id: string;
}

const VirtualSubMenu: FC<IProps> = ({ id }) => {
  const { t } = useI18n();
  const { config } = useConfig(id);
  const {
    mainMenu = [],
    secondMenu = []
  }: { mainMenu: IVirtualSubMenu[]; secondMenu: IVirtualSubMenu[] } = config;
  const { feed } = useVirtualSportsStore(['feed']);
  const { virtualSportsFrame } = useGeneralRoutes();
  const { pathname } = useLocation();
  const { isDesktop } = useDeviceStore();
  const { showTimeZoneSelector, isShowLanguageUserSettings, showSettings } =
    useUserSettings();

  const { providers } = feed;

  const getMenuItem = useCallback(
    ({
      providerId,
      categoryId,
      link
    }: IVirtualSportsMenuItemParams): IVirtualSportsSubMenuItem => ({
      title: t(`header.submenu.${providerId}`),
      id: categoryId,
      link,
      onClick: () => {
        logAction(LOG_ACTIONS.VSPORT_TOP_CATEGORY_CLICK, {
          CategoryName: t(`header.submenu.${providerId}`)
        });
      }
    }),
    []
  );

  const mainMenuItems = useMemo(() => {
    const feedMenuItems = providers.ids
      .map((providerId: string) => {
        const { categoryId, games: provideGames } =
          // @ts-expect-error TODO: move useVirtualSportsStore to TypeScript
          providers.data?.[providerId];
        const link = `${virtualSportsFrame}/${providerId}/${provideGames[0]}/`;
        return getMenuItem({ providerId, link, categoryId });
      })
      .filter(Boolean);

    const menuItemsConfig = mainMenu.map(({ title, ...rest }) => ({
      title: t(`header.submenu.${title}`),
      ...rest
    }));

    return [...menuItemsConfig, ...feedMenuItems];
  }, [t, providers, getMenuItem]);

  const filterSegments = useUserSegmentsIncludes();

  const navigationMenu = useMemo(
    // @ts-expect-error TODO: move useVirtualSportsStore to TypeScript
    () => mainMenuItems.filter((item) => filterSegments(item.userSegments)),
    [mainMenuItems, filterSegments]
  );

  const navigationOptions = useMemo(
    () =>
      secondMenu
        .map(({ title, ...rest }) => ({
          title: title ? t(`header.submenu.${title}`) : null,
          ...rest
        }))
        .filter((item) => filterSegments(item.userSegments)),
    [t, secondMenu, filterSegments]
  );

  const isActive = useCallback(
    (link: string) => {
      const pathWithProvider = getVSPath(pathname, false);

      if (pathWithProvider === virtualSportsFrame) {
        return getVSPath(pathname, true) === getVSPath(link, true);
      }

      return getRootPath(pathname) === link;
    },
    [pathname]
  );

  return (
    <CommonSubMenu
      mainMenuItems={navigationMenu}
      SecondaryItems={
        isDesktop && (
          <UserSettings
            items={navigationOptions}
            showTimeZoneSelector={showTimeZoneSelector}
            showLineSettings={false}
            isShowLanguageUserSettings={isShowLanguageUserSettings}
            showSettings={showSettings}
          />
        )
      }
      isActive={isActive}
    />
  );
};

export default VirtualSubMenu;
